/* eslint-disable no-param-reassign */
import { useEffect, useRef } from 'react';

import PatientModel from 'jsx/models/patient/patient';
import CONFIG from 'jsx/constants/config';
import errorReporter from 'jsx/utils/errorReporter';

declare global {
  interface Window {
    duet?: {
      open(): void;
      setMetadata: (metadata: {
        email?: string;
        name?: string;
        path?: string;
      }) => void;
      setUserId: (userId: string) => void;
      setUserIdAuth: (signature: string, epoch: number) => void;
    };
  }
}

interface SignatureToken {
  user_id: string;
  signature: string;
  epoch: number;
}

const onDecagonIframeLoad = (
  decagonIframe: HTMLElement,
  patient: PatientModel,
  tokenPromise: { current: Promise<SignatureToken | null> | null },
) => {
  window.duet?.setMetadata({
    email: patient.email,
    name: patient.fullName,
    path: window.location.pathname,
  });

  tokenPromise.current?.then((token) => {
    if (token) {
      window.duet?.setUserId(token.user_id);
      window.duet?.setUserIdAuth(token.signature, token.epoch);
    }
  });

  decagonIframe.style.bottom = '60px';
  decagonIframe.style.maxHeight = 'calc(100dvh - 60px)';

  decagonIframe.style.zIndex = '999994';
};

const decagonIframeLoaded = (
  brand: string,
  callback: (decagonIframe: HTMLElement) => void,
) => {
  setTimeout(() => {
    if (typeof document !== 'undefined') {
      const decagonIframe = document.querySelector(
        `[src="https://decagon.ai/demo/${brand}"]`,
      );

      if (decagonIframe) {
        decagonIframe.addEventListener('load', () =>
          callback(decagonIframe as HTMLElement),
        );
      } else {
        decagonIframeLoaded(brand, callback);
      }
    }
  }, 50);
};

export const useDecagonChatBot = (patient?: PatientModel) => {
  const brand = window.CUROLOGY_ENV.APP_BRAND ?? patient?.brand;
  const iframeListenerStarted = useRef(false);
  const tokenPromise = useRef<Promise<SignatureToken | null> | null>(null);

  useEffect(() => {
    if (!patient || CONFIG.isTestEnv) return;

    // ask for JSON so a 401 stays a 401 — else Laravel redirects and stores this url as url.intended for the next login
    tokenPromise.current = fetch('/api/v1/decagon/auth/signature-token', {
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .catch((err: unknown): null => {
        const status =
          err !== null && typeof err === 'object' && 'status' in err
            ? (err as { status: number }).status
            : undefined;
        if (status !== 401) {
          errorReporter.notifyException(
            new Error('Decagon signature token fetch failed'),
            { context: 'useDecagonChatBot', metadata: { status } },
          );
        }
        return null;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id]);

  useEffect(() => {
    // We only want this to run once.
    if (
      !!document.getElementById('decagon-script') ||
      !brand ||
      !patient ||
      CONFIG.isTestEnv
    ) {
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('id', 'decagon-script');
    script.src = `https://decagon.ai/loaders/${brand}.js`;
    script.async = true;

    document.body.appendChild(script);
  }, [brand, patient]);

  useEffect(() => {
    if (!brand || !patient || iframeListenerStarted.current) return;

    iframeListenerStarted.current = true;

    decagonIframeLoaded(brand, (decagonIframe) =>
      onDecagonIframeLoad(decagonIframe, patient, tokenPromise),
    );
  }, [brand, patient]);
};
